import React, { useEffect, useState } from 'react';
import api, { logout as doLogout } from '../api';
import { setTenantFeatures } from '../hooks/useFeature';
import AccountSwitcher from '../components/AccountSwitcher';
import StickyTop from '../components/StickyTop';
import '../dashboard.css';
import { canReadDocuments, userRoleLabel as roleLabel } from '../lib/roles';
import { pushState, enablePush, disablePush } from '../lib/push';

// Home — výber modulu po prihlásení. Dlaždice podľa roly a feature flagov,
// badge s počtom dokladov čakajúcich na akciu prihláseného usera.

const BASE = '';

interface Tile {
  key: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
  href: string;
  badge?: number | null;
  badgeLabel?: string;
  subline?: string | null;
  accent: string;
}

interface InvOverview {
  drafts: number;
  unpaid_count: number;
  unpaid_sum: string | number;
  overdue_count: number;
  overdue_sum: string | number;
  unmatched_transactions: number;
}

const fmtEur = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v) || 0;
  return `${n.toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const Home: React.FC = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin' || user.isSuperAdmin;
  // Zberač dokladov ('submitter') doklady iba nahráva — modul Schvaľovanie
  // sa mu vôbec neponúka a badge s počtom čakajúcich nemá čo ťahať.
  const seesDocuments = canReadDocuments(user);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<number | null>(null);
  const [invOverview, setInvOverview] = useState<InvOverview | null>(null);
  const [ordersPending, setOrdersPending] = useState<number | null>(null);

  useEffect(() => {
    api.get('/auth/tenant-meta').then(r => {
      setLogoUrl(r.data?.logo_url || null);
      setTenantName(r.data?.name || null);
      const f = r.data?.features || {};
      setFeatures(f);
      setTenantFeatures(f);
      // Prehľad fakturácie len keď je modul viditeľný (admin/účtovník + flag)
      if (f.invoicing === true && (user.role === 'admin' || user.role === 'accountant' || user.isSuperAdmin)) {
        api.get('/invoicing/overview').then(o => setInvOverview(o.data)).catch(() => {});
      }
      // Objednávky — badge s počtom čakajúcich na moje schválenie
      // (submitter modul nevidí, backend mu vracia 403)
      if (f.orders === true && user.role !== 'submitter') {
        api.get('/orders/pending-count').then(o => setOrdersPending(o.data?.count ?? 0)).catch(() => {});
      }
    }).catch(() => {});
    if (seesDocuments) {
      api.get('/documents/pending-count').then(r => setPending(r.data?.count ?? 0)).catch(() => setPending(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => doLogout();

  // Push notifikácie na tomto zariadení — zvonček v hlavičke (PWA vstupný bod)
  const [pushSt, setPushSt] = useState<'on' | 'off' | 'unsupported' | 'denied'>('unsupported');
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => { pushState().then(setPushSt).catch(() => {}); }, []);
  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushSt === 'on') await disablePush();
      else await enablePush();
      setPushSt(await pushState());
    } catch (e: any) {
      alert(e?.message || 'Notifikácie sa nepodarilo zapnúť');
      setPushSt(await pushState());
    } finally { setPushBusy(false); }
  };

  const iconStyle: React.CSSProperties = { width: 44, height: 44 };
  const tiles: Tile[] = [];

  // Schvaľovanie — pre všetkých okrem zberača dokladov (jadro systému)
  if (seesDocuments) tiles.push({
    key: 'approval',
    title: 'Schvaľovanie faktúr',
    desc: user.role === 'accountant'
      ? 'Prehľad dokladov, exporty pre účtovníctvo'
      : 'Prijaté doklady čakajúce na kontrolu a schválenie',
    href: '/dashboard',
    badge: pending,
    badgeLabel: user.role === 'accountant' ? 'na vybavenie' : 'čaká na teba',
    accent: '#2563eb',
    icon: (
      <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="m9 15 2 2 4-4" />
      </svg>
    ),
  });

  // Fakturácia — admin/účtovník + feature flag
  if ((isAdmin || user.role === 'accountant') && features.invoicing === true) {
    const sublines: string[] = [];
    if (invOverview) {
      if (invOverview.unpaid_count > 0) sublines.push(`${invOverview.unpaid_count} neuhradených · ${fmtEur(invOverview.unpaid_sum)}`);
      if (invOverview.unmatched_transactions > 0) sublines.push(`${invOverview.unmatched_transactions} nespárovaných platieb`);
    }
    tiles.push({
      key: 'invoicing',
      title: 'Fakturácia',
      desc: 'Vystavovanie faktúr s QR platbou, dobropisy a zálohy, import vystavených faktúr z PDF, párovanie úhrad z banky',
      href: '/invoices',
      badge: invOverview ? invOverview.overdue_count : null,
      badgeLabel: 'po splatnosti',
      subline: sublines.length ? sublines.join(' · ') : null,
      accent: '#16a34a',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v7.5m-3-3h6M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      ),
    });
  }

  // Účtovníctvo — mesačný balík pre účtovníčku; admin + účtovník, flag omega_export
  if (features.omega_export === true && (isAdmin || user.role === 'accountant')) {
    tiles.push({
      key: 'accounting',
      title: 'Účtovníctvo',
      desc: 'Mesačný balík pre účtovníčku — Omega TXT, PDF doklady a číselník partnerov v ZIP-e, aj s odoslaním emailom',
      href: '/accounting',
      accent: '#b45309',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
        </svg>
      ),
    });
  }

  // Objednávky — pre všetkých prihlásených okrem zberateľa dokladov,
  // keď má firma modul zapnutý
  if (features.orders === true && user.role !== 'submitter') {
    tiles.push({
      key: 'orders',
      title: 'Objednávky',
      desc: 'Interné objednávky so schvaľovaním — vystav, nechaj schváliť, pošli dodávateľovi',
      href: '/orders',
      badge: ordersPending,
      badgeLabel: 'čaká na teba',
      accent: '#0891b2',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007Z" />
        </svg>
      ),
    });
  }

  // Financie — čo má prísť, odhad DPH a dane z príjmu (admin/účtovník + flag)
  if ((isAdmin || user.role === 'accountant') && features.finance_overview === true) {
    tiles.push({
      key: 'finance',
      title: 'Financie',
      desc: 'Koľko má prísť a kedy, odhad DPH za obdobie a odhad dane z príjmu',
      href: '/finance',
      accent: '#7c3aed',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
        </svg>
      ),
    });
  }

  // Partneri — adresár + 360° história (zatiaľ viazané na fakturáciu)
  if ((isAdmin || user.role === 'accountant') && features.invoicing === true) {
    tiles.push({
      key: 'partners',
      title: 'Partneri',
      desc: 'Adresár odberateľov a dodávateľov — dopĺňa sa sám z dokladov, história partnera a export do Omegy',
      href: '/partners',
      accent: '#ea580c',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
        </svg>
      ),
    });
  }

  // Mobilný zber — pre všetkých
  tiles.push({
    key: 'capture',
    title: 'Nasnímať doklad',
    desc: 'Naskenuj QR a odfoť bloček či faktúru mobilom — doklad ide ako PDF rovno do schvaľovania',
    href: '/capture',
    accent: '#7c3aed',
    icon: (
      <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
      </svg>
    ),
  });

  // Admin panel
  if (isAdmin) {
    tiles.push({
      key: 'admin',
      title: 'Administrácia',
      desc: 'Používatelia, schvaľovacie pravidlá a úrovne, kategórie, podpis firmy a archív dokladov',
      href: '/admin',
      accent: '#6b7280',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      ),
    });
  }

  if (user.isSuperAdmin) {
    tiles.push({
      key: 'superadmin',
      title: 'Super admin',
      desc: 'Správa firiem, billing, integrácie, aktivita a história zmien',
      href: '/superadmin',
      accent: '#7c3aed',
      icon: (
        <svg style={iconStyle} fill="none" viewBox="0 0 24 24" strokeWidth={1.4} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
        </svg>
      ),
    });
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header + prepínač okruhov — ukotvené hore */}
      <StickyTop>
      <div style={{ background: '#fff', padding: '0.85rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          {logoUrl && <img src={`${BASE}${logoUrl}`} alt="logo" style={{ height: 44, width: 'auto', objectFit: 'contain' }} />}
          <h1 style={{ color: '#1f2937', margin: 0, fontSize: '1.25rem', lineHeight: 1.3 }}>Schvaľovanie<br />faktúr</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
          <div style={{ textAlign: 'right', minWidth: 0 }}>
            <div style={{ color: '#1f2937', fontWeight: 500, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{user.first_name} {user.last_name}</div>
            <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{roleLabel(user.role)}{tenantName ? ` · ${tenantName}` : ''}</div>
          </div>
          {pushSt !== 'unsupported' && (
            <button disabled={pushBusy || pushSt === 'denied'} onClick={togglePush}
              title={pushSt === 'denied' ? 'Notifikácie sú zablokované v nastaveniach prehliadača'
                : pushSt === 'on' ? 'Push notifikácie na tomto zariadení sú zapnuté — klik vypne'
                : 'Zapnúť push notifikácie na tomto zariadení (nový doklad na schválenie…)'}
              style={{ padding: '0.45rem 0.7rem', background: pushSt === 'on' ? '#eff6ff' : '#fff', color: pushSt === 'on' ? '#2563eb' : '#6b7280', border: `1px solid ${pushSt === 'on' ? '#bfdbfe' : '#d1d5db'}`, borderRadius: 6, cursor: pushSt === 'denied' ? 'default' : 'pointer', fontSize: '0.85rem', opacity: pushBusy ? 0.6 : 1 }}>
              {pushSt === 'on' ? '🔔' : '🔕'}
            </button>
          )}
          <button onClick={logout}
            style={{ padding: '0.45rem 0.9rem', background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
            Odhlásiť
          </button>
        </div>
      </div>

      <AccountSwitcher />
      </StickyTop>

      {/* Dlaždice */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
        <h2 style={{ color: '#374151', fontSize: 16, fontWeight: 600, margin: '0 0 18px' }}>Vyber si modul</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {tiles.map(t => (
            <a
              key={t.key}
              href={t.href}
              style={{
                display: 'block', background: '#fff', borderRadius: 12, padding: '22px 20px',
                textDecoration: 'none', color: '#1f2937', position: 'relative',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid transparent',
                transition: 'box-shadow .15s, transform .15s, border-color .15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = t.accent;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              {typeof t.badge === 'number' && t.badge > 0 && (
                <span style={{
                  position: 'absolute', top: 14, right: 14, background: '#dc2626', color: '#fff',
                  borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700,
                }}>
                  {t.badge} {t.badgeLabel}
                </span>
              )}
              <div style={{ color: t.accent, marginBottom: 12 }}>{t.icon}</div>
              <div style={{ fontSize: 16.5, fontWeight: 700, marginBottom: 4 }}>{t.title}</div>
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>{t.desc}</div>
              {t.subline && (
                <div style={{ fontSize: 12, color: '#374151', marginTop: 8, fontWeight: 600 }}>{t.subline}</div>
              )}
              {t.key === 'approval' && typeof t.badge === 'number' && t.badge === 0 && (
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 8, fontWeight: 600 }}>✓ Nič nečaká</div>
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
